import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../api/client';
import {
  WEEKDAY_FULL,
  formatDateShort,
  formatTimeFromISO,
  moscowDateISO,
  occurrenceDateMoscow,
} from '../utils/scheduleDates';
import { useAuth } from '../context/AuthContext';

export default function MySchedulePage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [children, setChildren] = useState([]);
  const [requests, setRequests] = useState([]);
  const [reqBusyKey, setReqBusyKey] = useState('');
  const [reqNote, setReqNote] = useState({});
  const [reqProposed, setReqProposed] = useState({});
  const [reqError, setReqError] = useState('');
  const [reqOk, setReqOk] = useState('');

  const studentId = searchParams.get('student_id') || '';

  useEffect(() => {
    if (!user?.is_parent) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/parent/children/');
        if (!cancelled) {
          const list = Array.isArray(res.data) ? res.data : res.data?.results || [];
          setChildren(list);
        }
      } catch {
        if (!cancelled) setChildren([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const params = {};
        if (studentId) params.student_id = studentId;
        const [sched, changeReqs] = await Promise.all([
          api.get('/scheduler/me/', { params }),
          api.get('/scheduler/me/change-requests/', { params }).catch(() => ({ data: [] })),
        ]);
        if (!cancelled) {
          setData(sched.data);
          setRequests(Array.isArray(changeReqs.data) ? changeReqs.data : []);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.detail || 'Не удалось загрузить расписание');
          setData(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [studentId]);

  async function submitChangeRequest(occ, kind) {
    setReqError('');
    setReqOk('');
    setReqBusyKey(`${occ.key}:${kind}`);
    try {
      const payload = {
        teacher_id: occ.teacher_id,
        occurrence_key: occ.key,
        occurrence_start: occ.start,
        weekly_slot_id: occ.weekly_slot_id || null,
        oneoff_id: occ.oneoff_id || null,
        original_date: occ.original_date || occurrenceDateMoscow(occ.start),
        kind,
        note: (reqNote[occ.key] || '').trim(),
      };
      if (studentId) payload.student_id = Number(studentId);
      if (kind === 'reschedule') {
        const proposed = (reqProposed[occ.key] || '').trim();
        if (!proposed) {
          setReqError('Укажите желаемое новое время (локальное, например 2026-09-12T17:00).');
          return;
        }
        payload.proposed_new_start = proposed;
      }
      const res = await api.post('/scheduler/me/change-requests/', payload);
      setRequests((prev) => [res.data, ...prev]);
      setReqOk(kind === 'cancel' ? 'Запрос на отмену отправлен' : 'Запрос на перенос отправлен');
    } catch (err) {
      setReqError(err.response?.data?.detail || 'Не удалось отправить запрос');
    } finally {
      setReqBusyKey('');
    }
  }

  const today = moscowDateISO();
  const upcoming = (data?.occurrences || []).filter((o) => !o.is_cancelled);
  const cancelled = (data?.occurrences || []).filter((o) => o.is_cancelled);
  const weekly = data?.weekly_pattern || [];
  const pendingKeys = new Set(
    requests.filter((r) => r.status === 'pending').map((r) => r.occurrence_key),
  );

  return (
    <div className="sched-page sched-page--student">
      <div className="page-header">
        <h1>Моё расписание</h1>
        <p className="page-subtitle">
          Постоянное расписание и ближайшие даты (с отменами и переносами). Прошедшие — в{' '}
          <Link to="/my-lessons">журнале занятий</Link>
          {' · '}
          <Link to="/my-messages">сообщения</Link>.
        </p>
      </div>

      {user?.is_parent && children.length > 0 && (
        <label className="sched-filter">
          Ребёнок
          <select
            value={studentId}
            onChange={(e) => {
              const v = e.target.value;
              if (v) setSearchParams({ student_id: v });
              else setSearchParams({});
            }}
          >
            <option value="">По умолчанию</option>
            {children.map((c) => (
              <option key={c.id} value={c.id}>
                {c.display_name || c.first_name || c.username}
              </option>
            ))}
          </select>
        </label>
      )}

      {error && <div className="alert alert-error">{error}</div>}
      {loading && <p>Загрузка…</p>}

      {!loading && data && (
        <>
          {data.exam && data.exam.exam_date && (
            <div className="sched-exam-banner">
              До экзамена {data.exam.exam_date}: <strong>{data.exam.count}</strong> занятий
              {data.exam.holiday_hits?.length > 0 && (
                <span className="muted">
                  {' '}
                  (+{data.exam.holiday_hits.length} на праздники — уточните у преподавателя)
                </span>
              )}
            </div>
          )}

          <section className="sched-panel">
            <h2>Обычное расписание</h2>
            <p className="muted">Постоянные слоты каждую неделю (без разовых отмен).</p>
            {weekly.length === 0 ? (
              <p className="muted">Пока нет постоянного слота.</p>
            ) : (
              <ul className="sched-upcoming-list">
                {weekly.map((slot) => (
                  <li key={`${slot.teacher_id}-${slot.id}`}>
                    <div className="sched-upcoming-date">
                      {WEEKDAY_FULL[slot.weekday]}, {slot.start_time}
                    </div>
                    <div className="sched-upcoming-meta">
                      {slot.duration_minutes} мин
                      {slot.is_group ? ` · группа ${slot.enrolled_count}/${slot.max_students}` : ''}
                      {slot.teacher_name ? ` · ${slot.teacher_name}` : ''}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="sched-panel">
            <h2>Ближайшие даты</h2>
            <p className="muted">
              Конкретные занятия с {data.from} по {data.to}, с учётом отмен и переносов.
              Перенос или отмену можно запросить кнопками — преподаватель подтвердит в очереди.
            </p>
            {reqError && <div className="alert alert-error">{reqError}</div>}
            {reqOk && <div className="alert alert-success">{reqOk}</div>}
            {upcoming.length === 0 ? (
              <p className="muted">В этом периоде нет запланированных занятий.</p>
            ) : (
              <ul className="sched-upcoming-list">
                {upcoming.map((occ) => {
                  const d = occurrenceDateMoscow(occ.start);
                  const isToday = d === today;
                  const pending = pendingKeys.has(occ.key);
                  return (
                    <li key={occ.key} className={isToday ? 'is-today' : ''}>
                      <div className="sched-upcoming-date">
                        {isToday ? 'Сегодня' : formatDateShort(d)}
                        {', '}
                        {formatTimeFromISO(occ.start)}
                        {occ.kind === 'rescheduled' ? ' · перенос' : ''}
                        {occ.kind === 'oneoff' ? ' · разовое' : ''}
                      </div>
                      <div className="sched-upcoming-meta">
                        {occ.duration_minutes} мин
                        {occ.is_group ? ` · группа ${occ.enrolled_count}/${occ.max_students}` : ''}
                        {occ.teacher_name ? ` · ${occ.teacher_name}` : ''}
                        {occ.on_holiday ? ` · ${occ.holiday_name || 'праздник'}` : ''}
                      </div>
                      <div className="ops-change-actions">
                        {pending ? (
                          <span className="muted">Запрос уже в очереди у преподавателя</span>
                        ) : (
                          <>
                            <input
                              className="form-input ops-change-note"
                              type="text"
                              placeholder="Комментарий (необязательно)"
                              value={reqNote[occ.key] || ''}
                              onChange={(e) => setReqNote((prev) => ({ ...prev, [occ.key]: e.target.value }))}
                            />
                            <input
                              className="form-input ops-change-note"
                              type="text"
                              placeholder="Новое время ISO, напр. 2026-09-12T17:00"
                              value={reqProposed[occ.key] || ''}
                              onChange={(e) => setReqProposed((prev) => ({ ...prev, [occ.key]: e.target.value }))}
                            />
                            <button
                              type="button"
                              className="btn btn-sm btn-outline"
                              disabled={!!reqBusyKey}
                              onClick={() => submitChangeRequest(occ, 'cancel')}
                            >
                              {reqBusyKey === `${occ.key}:cancel` ? '…' : 'Отменить'}
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline"
                              disabled={!!reqBusyKey}
                              onClick={() => submitChangeRequest(occ, 'reschedule')}
                            >
                              {reqBusyKey === `${occ.key}:reschedule` ? '…' : 'Перенести'}
                            </button>
                          </>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          {requests.length > 0 && (
            <section className="sched-panel">
              <h2>Мои запросы</h2>
              <ul className="sched-upcoming-list">
                {requests.slice(0, 20).map((r) => (
                  <li key={r.id}>
                    <div className="sched-upcoming-date">
                      {r.kind === 'cancel' ? 'Отмена' : 'Перенос'} · {r.status}
                      {r.occurrence_start ? ` · ${formatDateShort(occurrenceDateMoscow(r.occurrence_start))} ${formatTimeFromISO(r.occurrence_start)}` : ''}
                    </div>
                    <div className="sched-upcoming-meta">
                      {r.teacher_name}
                      {r.note ? ` · ${r.note}` : ''}
                      {r.teacher_note ? ` · ответ: ${r.teacher_note}` : ''}
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {cancelled.length > 0 && (
            <section className="sched-panel">
              <h2>Отмены в периоде</h2>
              <p className="muted">Разовые отмены или старое место после переноса — шаблон не менялся.</p>
              <ul className="sched-upcoming-list sched-upcoming-list--muted">
                {cancelled.map((occ) => (
                  <li key={occ.key}>
                    <div className="sched-upcoming-date">
                      {formatDateShort(occurrenceDateMoscow(occ.start))}, {formatTimeFromISO(occ.start)} — отменено
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}
    </div>
  );
}
